import { trackEvent } from "@/utils/analytics";
import { calculateTimeDifference } from "@/utils/checkout-analytics";
import logger from "@/utils/logger";

/**
 * Conversion Funnel Tracking Utility
 * Tracks user progression through the checkout conversion funnel
 */

// Funnel step definitions
export const FUNNEL_STEPS = {
  PRICING_VIEW: "pricing_view",
  PRICING_TIER_SELECTED: "pricing_tier_selected", 
  CHECKOUT_INITIATED: "checkout_initiated",
  FORM_STARTED: "form_started",
  FORM_COMPLETED: "form_completed",
  PAYMENT_SUBMITTED: "payment_submitted",
  PAYMENT_SUCCESS: "payment_success",
  CHECKOUT_SUCCESS_PAGE_LOADED: "checkout_success_page_loaded",
  SESSION_DETAILS_RETRIEVED: "session_details_retrieved",
  ACCOUNT_CREATION_STARTED: "account_creation_started",
  ACCOUNT_CREATION_SUCCESS: "account_creation_success",
  AUTH_COMPLETED: "auth_completed",
  FLOW_COMPLETED: "flow_completed",
};

// Error step tracking
export const ERROR_STEPS = {
  PAYMENT_FAILED: "payment_failed",
  ACCOUNT_CREATION_FAILED: "account_creation_failed",
  AUTH_FAILED: "auth_failed",
  FORM_VALIDATION_FAILED: "form_validation_failed",
  NETWORK_ERROR: "network_error",
};

// Success metrics categories
export const SUCCESS_METRICS = {
  PAYMENT_SUCCESS_RATE: "payment_success_rate",
  ACCOUNT_CREATION_SUCCESS_RATE: "account_creation_success_rate",
  OVERALL_CONVERSION_RATE: "overall_conversion_rate",
  TIME_TO_COMPLETION: "time_to_completion",
};

/**
 * Funnel session manager for tracking user progression
 */
class FunnelSession {
  constructor(sessionId = null) {
    this.sessionId = sessionId || this.generateSessionId();
    this.startTime = new Date();
    this.steps = [];
    this.currentStep = null;
    this.errors = [];
    this.metadata = {};
  }

  generateSessionId() {
    return `funnel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Record a funnel step completion
   * @param {string} step - Funnel step name
   * @param {Object} stepData - Additional step data
   */
  recordStep(step, stepData = {}) {
    const stepRecord = {
      step: step,
      timestamp: new Date().toISOString(),
      timeFromStart: calculateTimeDifference(this.startTime),
      timeFromPrevious: this.currentStep ? 
        calculateTimeDifference(this.steps[this.steps.length - 1]?.timestamp || this.startTime) : 0,
      ...stepData,
    };

    this.steps.push(stepRecord);
    this.currentStep = step;

    // Track the step in analytics
    this.trackFunnelStep(step, stepRecord);

    logger.log(`Funnel step recorded: ${step}`, stepRecord);
    return stepRecord;
  }

  /**
   * Record a funnel error
   * @param {string} errorStep - Error step name
   * @param {Object} errorData - Error details
   */
  recordError(errorStep, errorData = {}) {
    const errorRecord = {
      errorStep: errorStep,
      timestamp: new Date().toISOString(),
      timeFromStart: calculateTimeDifference(this.startTime),
      currentStep: this.currentStep,
      ...errorData,
    };

    this.errors.push(errorRecord);

    // Track the error in analytics
    this.trackFunnelError(errorStep, errorRecord);

    logger.error(`Funnel error recorded: ${errorStep}`, errorRecord);
    return errorRecord;
  }

  /**
   * Add metadata to the funnel session
   * @param {Object} metadata - Metadata to add
   */
  addMetadata(metadata) {
    this.metadata = { ...this.metadata, ...metadata };
  }

  /**
   * Get funnel completion rate for this session
   * @returns {number} Completion percentage (0-100)
   */
  getCompletionRate() {
    const totalSteps = Object.keys(FUNNEL_STEPS).length;
    const completedSteps = this.steps.length;
    return Math.round((completedSteps / totalSteps) * 100);
  }

  /**
   * Get time to completion
   * @returns {number} Time in milliseconds
   */
  getTimeToCompletion() {
    return calculateTimeDifference(this.startTime);
  }

  /**
   * Check if the funnel was completed successfully
   * @returns {boolean} True if completed
   */
  isCompleted() {
    return this.steps.some(step => step.step === FUNNEL_STEPS.FLOW_COMPLETED);
  }

  /**
   * Get funnel abandonment point
   * @returns {string|null} Last completed step before abandonment
   */
  getAbandonmentPoint() {
    if (this.isCompleted()) return null;
    return this.currentStep;
  }

  /**
   * Track funnel step in analytics
   * @param {string} step - Step name
   * @param {Object} stepData - Step data
   */
  trackFunnelStep(step, stepData) {
    return trackEvent("conversion_funnel_step", {
      session_id: this.sessionId,
      funnel_step: step,
      step_order: this.steps.length,
      completion_rate: this.getCompletionRate(),
      time_from_start: stepData.timeFromStart,
      time_from_previous: stepData.timeFromPrevious,
      ...stepData,
    });
  }

  /**
   * Track funnel error in analytics
   * @param {string} errorStep - Error step name
   * @param {Object} errorData - Error data
   */
  trackFunnelError(errorStep, errorData) {
    return trackEvent("conversion_funnel_error", {
      session_id: this.sessionId,
      error_step: errorStep,
      current_step: this.currentStep,
      steps_completed: this.steps.length,
      time_from_start: errorData.timeFromStart,
      ...errorData,
    });
  }

  /**
   * Generate final funnel report
   * @returns {Object} Complete funnel session data
   */
  generateReport() {
    return {
      sessionId: this.sessionId,
      startTime: this.startTime,
      endTime: new Date(),
      totalTime: this.getTimeToCompletion(),
      steps: this.steps,
      errors: this.errors,
      metadata: this.metadata,
      completionRate: this.getCompletionRate(),
      isCompleted: this.isCompleted(),
      abandonmentPoint: this.getAbandonmentPoint(),
      stepCount: this.steps.length,
      errorCount: this.errors.length,
    };
  }
}

/**
 * Global funnel session manager
 */
let currentFunnelSession = null;

/**
 * Start a new funnel tracking session
 * @param {Object} initialData - Initial session data
 * @returns {FunnelSession} New funnel session
 */
export const startFunnelSession = (initialData = {}) => {
  currentFunnelSession = new FunnelSession();
  currentFunnelSession.addMetadata(initialData);
  
  logger.log("Funnel session started", {
    sessionId: currentFunnelSession.sessionId,
    initialData,
  });

  return currentFunnelSession;
};

/**
 * Get the current funnel session
 * @returns {FunnelSession|null} Current session or null
 */
export const getCurrentFunnelSession = () => {
  return currentFunnelSession;
};

/**
 * Record a step in the current funnel session
 * @param {string} step - Step name
 * @param {Object} stepData - Step data
 */
export const recordFunnelStep = (step, stepData = {}) => {
  if (!currentFunnelSession) {
    logger.warn("No active funnel session, starting new one");
    startFunnelSession();
  }
  
  return currentFunnelSession.recordStep(step, stepData);
};

/**
 * Record an error in the current funnel session
 * @param {string} errorStep - Error step name
 * @param {Object} errorData - Error data
 */
export const recordFunnelError = (errorStep, errorData = {}) => {
  if (!currentFunnelSession) {
    logger.warn("No active funnel session for error recording");
    return null;
  }
  
  return currentFunnelSession.recordError(errorStep, errorData);
};

/**
 * Complete the current funnel session
 * @param {Object} completionData - Final completion data
 */
export const completeFunnelSession = (completionData = {}) => {
  if (!currentFunnelSession) {
    logger.warn("No active funnel session to complete");
    return null;
  }

  // Record the final completion step
  currentFunnelSession.recordStep(FUNNEL_STEPS.FLOW_COMPLETED, completionData);

  // Generate and track final report
  const report = currentFunnelSession.generateReport();
  
  trackEvent("conversion_funnel_completed", {
    session_id: report.sessionId,
    total_time: report.totalTime,
    step_count: report.stepCount,
    error_count: report.errorCount,
    completion_rate: report.completionRate,
    ...completionData,
  });

  logger.log("Funnel session completed", report);

  // Clear the current session
  currentFunnelSession = null;

  return report;
};

/**
 * Abandon the current funnel session
 * @param {string} reason - Reason for abandonment
 * @param {Object} abandonmentData - Additional abandonment data
 */
export const abandonFunnelSession = (reason = "user_exit", abandonmentData = {}) => {
  if (!currentFunnelSession) {
    return null;
  }

  const report = currentFunnelSession.generateReport();
  
  trackEvent("conversion_funnel_abandoned", {
    session_id: report.sessionId,
    abandonment_reason: reason,
    abandonment_point: report.abandonmentPoint,
    steps_completed: report.stepCount,
    time_spent: report.totalTime,
    completion_rate: report.completionRate,
    ...abandonmentData,
  });

  logger.log("Funnel session abandoned", { reason, ...report });

  // Clear the current session
  currentFunnelSession = null;

  return report;
};

/**
 * Track payment success metrics
 * @param {Object} paymentData - Payment success data
 */
export const trackPaymentSuccess = (paymentData = {}) => {
  const eventData = {
    metric_type: SUCCESS_METRICS.PAYMENT_SUCCESS_RATE,
    success: true,
    timestamp: new Date().toISOString(),
    ...paymentData,
  };

  recordFunnelStep(FUNNEL_STEPS.PAYMENT_SUCCESS, eventData);
  return trackEvent("payment_success_metric", eventData);
};

/**
 * Track payment failure metrics
 * @param {Object} failureData - Payment failure data
 */
export const trackPaymentFailure = (failureData = {}) => {
  const eventData = {
    metric_type: SUCCESS_METRICS.PAYMENT_SUCCESS_RATE,
    success: false,
    timestamp: new Date().toISOString(),
    ...failureData,
  };

  recordFunnelError(ERROR_STEPS.PAYMENT_FAILED, eventData);
  return trackEvent("payment_failure_metric", eventData);
};

/**
 * Track account creation success metrics
 * @param {Object} accountData - Account creation success data
 */
export const trackAccountCreationSuccess = (accountData = {}) => {
  const eventData = {
    metric_type: SUCCESS_METRICS.ACCOUNT_CREATION_SUCCESS_RATE,
    success: true,
    timestamp: new Date().toISOString(),
    ...accountData,
  };

  recordFunnelStep(FUNNEL_STEPS.ACCOUNT_CREATION_SUCCESS, eventData);
  return trackEvent("account_creation_success_metric", eventData);
};

/**
 * Track account creation failure metrics
 * @param {Object} failureData - Account creation failure data
 */
export const trackAccountCreationFailure = (failureData = {}) => {
  const eventData = {
    metric_type: SUCCESS_METRICS.ACCOUNT_CREATION_SUCCESS_RATE,
    success: false,
    timestamp: new Date().toISOString(),
    ...failureData,
  };

  recordFunnelError(ERROR_STEPS.ACCOUNT_CREATION_FAILED, eventData);
  return trackEvent("account_creation_failure_metric", eventData);
};

/**
 * Track time-to-completion metrics
 * @param {number} completionTime - Time to completion in milliseconds
 * @param {Object} timingData - Additional timing data
 */
export const trackTimeToCompletion = (completionTime, timingData = {}) => {
  const eventData = {
    metric_type: SUCCESS_METRICS.TIME_TO_COMPLETION,
    completion_time_ms: completionTime,
    completion_time_seconds: Math.round(completionTime / 1000),
    completion_time_minutes: Math.round(completionTime / 60000),
    timestamp: new Date().toISOString(),
    ...timingData,
  };

  return trackEvent("time_to_completion_metric", eventData);
};